/**
 * Entity components — Timeline and PriceBreakdown.
 *
 * PriceBreakdown is the one place a devotee's or a pandit's money story is rendered, and its
 * `pandit` variant shows **the same lines, in the same order, with the same labels** the pandit
 * sees in the app. If it reads badly here, it reads badly to them — that is the point of showing
 * it to an operator at all.
 */
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from './primitives';
import type { IconName } from './icons';

/* ────────────────────────────  Timeline  ──────────────────────────── */

export type TimelineEventState = 'done' | 'current' | 'future' | 'money' | 'error';

export interface TimelineEvent {
  title: string;
  timestamp?: string;
  detail?: string;
  /** Pre-formatted by lib/money — the timeline never formats money itself. */
  amount?: string;
  state?: TimelineEventState;
  icon?: IconName;
}

const NODE: Record<TimelineEventState, { color: string; icon: IconName }> = {
  done: { color: 'var(--c-success)', icon: 'circle-check' },
  current: { color: 'var(--c-saffron)', icon: 'clock' },
  future: { color: 'var(--c-ink3)', icon: 'circle' },
  money: { color: 'var(--c-info)', icon: 'indian-rupee' },
  error: { color: 'var(--c-error)', icon: 'circle-alert' },
};

export function Timeline({ events, style }: { events: readonly TimelineEvent[]; style?: CSSProperties }) {
  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', ...style }}>
      {events.map((e, i) => {
        const state = e.state ?? 'future';
        const node = NODE[state];
        const last = i === events.length - 1;
        return (
          <li key={`${e.title}-${i}`} style={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 'var(--sp-md)' }}>
            <div style={{ display: 'grid', justifyItems: 'center', gap: 2 }}>
              <Icon name={e.icon ?? node.icon} size={16} style={{ color: node.color }} />
              {/* The rail stops at the last node rather than trailing into nothing. */}
              {!last && <span style={{ width: 1, flex: 1, minHeight: 18, background: 'var(--c-border)' }} />}
            </div>
            <div style={{ display: 'grid', gap: 2, paddingBottom: last ? 0 : 'var(--sp-md)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
                <span
                  style={{
                    fontSize: 'var(--fs-admin-ui)',
                    color: state === 'current' ? 'var(--c-ink)' : 'var(--c-ink2)',
                    fontWeight: state === 'current' ? 'var(--fw-semibold)' : 'var(--fw-regular)',
                  }}
                >
                  {e.title}
                </span>
                {e.amount && (
                  <span className="mono-num tabular" style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink)' }}>
                    {e.amount}
                  </span>
                )}
                <span style={{ flex: 1 }} />
                {e.timestamp && (
                  <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>{e.timestamp}</span>
                )}
              </div>
              {e.detail && (
                <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>{e.detail}</span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* ────────────────────────────  PriceBreakdown  ──────────────────────────── */

export interface PriceLine {
  label: string;
  /** Pre-formatted by lib/money.ts. This component never does arithmetic. */
  amount: string;
  /** A deduction — rendered with the explicit − already in the formatted string. */
  discount?: boolean;
}

export interface PriceBreakdownProps {
  variant?: 'admin' | 'pandit';
  lines: readonly PriceLine[];
  total: string;
  footnote?: string;
  /** e.g. "Draft v15 — devotees still see v14 until you publish." */
  notice?: string;
  style?: CSSProperties;
}

export function PriceBreakdown({ variant = 'admin', lines, total, footnote, notice, style }: PriceBreakdownProps) {
  return (
    <div
      style={{
        border: '1px solid var(--c-border)',
        borderRadius: 'var(--r-tile)',
        background: 'var(--c-surface2)',
        overflow: 'hidden',
        ...style,
      }}
    >
      {notice && (
        <div
          style={{
            padding: 'var(--sp-sm) var(--sp-md)',
            background: 'var(--c-warn-bg)',
            color: 'var(--c-warn-text)',
            fontSize: 'var(--fs-admin-colhead)',
            borderBottom: '1px solid var(--c-warn)',
          }}
        >
          {notice}
        </div>
      )}
      <div style={{ display: 'grid' }}>
        {lines.map((l, i) => (
          <div
            key={l.label}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 'var(--sp-md)',
              padding: '7px var(--sp-md)',
              borderTop: i ? '1px solid var(--c-border)' : 'none',
              fontSize: 'var(--fs-admin-ui)',
            }}
          >
            <span style={{ flex: 1, color: l.discount ? 'var(--c-ink3)' : 'var(--c-ink2)' }}>{l.label}</span>
            <span
              className="mono-num tabular"
              style={{ color: l.discount ? 'var(--c-error-text)' : 'var(--c-ink)', whiteSpace: 'nowrap' }}
            >
              {l.amount}
            </span>
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--sp-md)',
          padding: 'var(--sp-md)',
          borderTop: '1px solid var(--c-border-strong)',
          background: 'var(--c-sunk)',
        }}
      >
        <span
          style={{
            flex: 1,
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--fs-admin-card-heading)',
            fontWeight: 'var(--fw-semibold)',
            color: 'var(--c-ink)',
          }}
        >
          {variant === 'pandit' ? 'Net payable' : 'Total'}
        </span>
        <span
          className="mono-num tabular"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--fs-admin-card-heading)',
            fontWeight: 'var(--fw-semibold)',
            color: 'var(--c-ink)',
          }}
        >
          {total}
        </span>
      </div>
      {footnote && (
        <div
          style={{
            padding: 'var(--sp-sm) var(--sp-md)',
            borderTop: '1px solid var(--c-border)',
            fontSize: 'var(--fs-admin-colhead)',
            color: 'var(--c-ink3)',
          }}
        >
          {footnote}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────  KV  ──────────────────────────── */

export type KVRow = readonly [ReactNode, ReactNode];

/** The read-only detail pattern: label left, value right-aligned, hairline between. */
export function KV({ rows, style }: { rows: readonly KVRow[]; style?: CSSProperties }) {
  return (
    <dl style={{ display: 'grid', gap: 0, margin: 0, ...style }}>
      {rows.map(([label, value], i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 'var(--sp-md)',
            alignItems: 'baseline',
            padding: 'var(--sp-sm) 0',
            borderTop: i ? '1px solid var(--c-border)' : 'none',
            fontSize: 'var(--fs-admin-ui)',
          }}
        >
          <dt style={{ flex: '0 0 168px', color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-cell)' }}>{label}</dt>
          <dd style={{ flex: 1, margin: 0, color: 'var(--c-ink)', textAlign: 'right' }}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
